import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * App-level error boundary. Catches render/runtime errors in the subtree
 * and renders a friendly fallback using the new design tokens. Errors are
 * still logged to the console so they are not swallowed silently.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to the console — do not swallow.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught error:', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message || 'An unexpected error occurred.';
      return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
          <div className="flex flex-col items-center justify-center text-center rounded-xl border border-dashed border-ink-200 dark:border-ink-700 bg-ink-50/40 dark:bg-ink-900/30 p-10 max-w-lg w-full">
            <div className="mb-3 w-10 h-10 rounded-full bg-ink-100 dark:bg-ink-800 flex items-center justify-center text-ink-500">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="font-display text-sm font-semibold text-foreground">
              Something went wrong
            </div>
            <div className="mt-1 text-[12px] text-ink-500 max-w-sm">
              {message}
            </div>
            <div className="mt-2 text-[11px] text-ink-500">
              The error has been logged. Try reloading the page.
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-ink-950 text-white dark:bg-gold-500 dark:text-ink-950 hover:opacity-90 transition-opacity"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
